import {
  For,
  Show,
  createSignal,
  onMount,
  onCleanup,
  type JSX,
} from 'solid-js';
import { t } from '@/shared/i18n';

export interface ExportOption {
  /** Unique key for the option */
  key: string;
  /** Display label */
  label: () => string;
  /** Icon SVG (optional) */
  icon?: () => JSX.Element;
  /** Click handler */
  onSelect: () => void;
  /** Disable the option (e.g. while loading) */
  disabled?: () => boolean;
  /** Optional description shown below the label */
  description?: () => string;
}

export interface ExportGroup {
  /** Unique key for the group */
  key: string;
  /** Group label (e.g. "Download", "Print (3 selected)") */
  label: () => string;
  /** Options within this group */
  options: ExportOption[];
}

interface ExportMenuProps {
  /** Grouped export options */
  groups: ExportGroup[];
  /** Whether any export is in progress */
  loading?: () => boolean;
  /** Trigger variant — 'default' shows Export button, 'compact' shows Actions button */
  variant?: 'default' | 'compact';
}

// Icons
const DownloadIcon = () => (
  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width={1.5}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const EllipsisIcon = () => (
  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width={2}
      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
    />
  </svg>
);

export function ExportMenu(props: ExportMenuProps) {
  const [open, setOpen] = createSignal(false);
  let containerRef: HTMLDivElement | undefined;

  const isLoading = () => props.loading?.() ?? false;
  const isCompact = () => props.variant === 'compact';

  // Close on outside click
  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef && !containerRef.contains(e.target as Node)) {
      setOpen(false);
    }
  };

  // Close on Escape
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleKeyDown);
  });

  const toggle = () => setOpen((prev) => !prev);

  const handleSelect = (option: ExportOption) => {
    if (option.disabled?.()) return;
    option.onSelect();
    setOpen(false);
  };

  return (
    <div ref={containerRef} class="relative">
      <button
        type="button"
        class={
          isCompact()
            ? 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-border-default bg-bg-surface px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-border-focus disabled:cursor-not-allowed disabled:opacity-50'
            : 'inline-flex items-center justify-center gap-2 rounded-lg border border-border-default bg-bg-surface px-4 py-2 text-base font-medium text-text-primary transition-colors hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-border-focus disabled:cursor-not-allowed disabled:opacity-50'
        }
        disabled={isLoading()}
        onClick={toggle}
      >
        <Show when={!isCompact()} fallback={<EllipsisIcon />}>
          <DownloadIcon />
        </Show>
        {isLoading()
          ? t('export.exporting')
          : isCompact()
            ? t('export.actions')
            : t('export.title')}
        <ChevronDownIcon />
      </button>

      <Show when={open()}>
        <div class="absolute right-0 z-50 mt-1 min-w-[220px] overflow-hidden rounded-lg border border-border-subtle bg-bg-surface shadow-lg">
          <For each={props.groups}>
            {(group, groupIndex) => (
              <>
                <Show when={groupIndex() > 0}>
                  <div class="mx-2 my-1 h-px bg-border-subtle" />
                </Show>
                <div class="px-3 pb-1 pt-2">
                  <span class="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {group.label()}
                  </span>
                </div>
                <div class="p-1">
                  <For each={group.options}>
                    {(option) => (
                      <button
                        type="button"
                        class="flex w-full cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-hover disabled:cursor-not-allowed disabled:text-text-muted"
                        disabled={option.disabled?.()}
                        onClick={() => handleSelect(option)}
                      >
                        <Show when={option.icon} fallback={<DownloadIcon />}>
                          {option.icon!()}
                        </Show>
                        <div class="flex flex-col">
                          <span class="font-medium">{option.label()}</span>
                          <Show when={option.description}>
                            <span class="text-xs text-text-secondary">
                              {option.description!()}
                            </span>
                          </Show>
                        </div>
                      </button>
                    )}
                  </For>
                </div>
              </>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
